import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { ReactComponent as LogoMark } from '../assets/images/logo-mark.svg';
// eslint-disable-next-line no-unused-vars
import { T, font } from '../theme';
import { useTheme } from '../context/ThemeContext';

const NAV_LINKS = [
  { to: '/dashboard', label: 'Overview' },
  { to: '/projects',  label: 'Projects' },
  { to: '/sessions',  label: 'Sessions' },
  { to: '/chat',      label: 'Research Chat ✦' },
  { to: '/keys',      label: 'API Keys' },
  { to: '/billing',   label: 'Billing' },
];

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="8" y1="1" x2="8" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="1" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="13" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="2.93" y1="2.93" x2="4.34" y2="4.34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="11.66" y1="11.66" x2="13.07" y2="13.07" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="11.66" y1="4.34" x2="13.07" y2="2.93" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="2.93" y1="13.07" x2="4.34" y2="11.66" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BurgerIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    {open ? (
      <>
        <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    ) : (
      <>
        <line x1="3" y1="6"  x2="17" y2="6"  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="3" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    )}
  </svg>
);

export default function Layout({ children, fullHeight = false }) {
  const location = useLocation();
  const { user } = useUser();
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const rootStyle = {
    minHeight: '100vh',
    background: 'var(--color-bg)',
    fontFamily: 'var(--font-sans)',
    color: 'var(--color-text)',
  };

  return (
    <div style={rootStyle}>
      <nav style={s.nav}>
        <div style={s.navInner} className="scq-portal-nav-inner">
          <Link to="/dashboard" style={s.logo}>
            <LogoMark width="22" height="22" style={{flexShrink:0}} />
            SenseCritiq
          </Link>
          <div style={s.navLinks} className="scq-portal-nav-links">
            {NAV_LINKS.map(({ to, label }) => {
              const active = location.pathname === to;
              return (
                <Link key={to} to={to} style={{...s.navLink, ...(active ? s.navLinkActive : {})}}>
                  {label}
                </Link>
              );
            })}
          </div>
          <div style={s.navRight}>
            <span style={s.navEmail} className="scq-portal-nav-email">{user?.primaryEmailAddress?.emailAddress}</span>
            <UserButton afterSignOutUrl="/sign-in" />
            <button
              onClick={toggleTheme}
              title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
              style={s.themeBtn}
            >
              {isLight ? <MoonIcon /> : <SunIcon />}
            </button>
            <button
              style={s.burger}
              className="scq-portal-burger"
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <BurgerIcon open={menuOpen} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="scq-portal-overlay" onClick={() => setMenuOpen(false)}>
          <div className="scq-portal-drawer" onClick={e => e.stopPropagation()}>
            <div className="scq-portal-drawer-header">
              <Link to="/dashboard" style={s.logo} onClick={() => setMenuOpen(false)}>
                <LogoMark width="22" height="22" style={{flexShrink:0}} />
                SenseCritiq
              </Link>
              <button style={s.drawerClose} onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <BurgerIcon open={true} />
              </button>
            </div>
            <div className="scq-portal-drawer-links">
              {NAV_LINKS.map(({ to, label }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`scq-portal-drawer-link${active ? ' scq-portal-drawer-link-active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
            <div className="scq-portal-drawer-footer">
              {user?.primaryEmailAddress?.emailAddress}
            </div>
          </div>
        </div>
      )}

      <main style={fullHeight ? s.mainFull : s.main}>{children}</main>
    </div>
  );
}

const s = {
  nav: { background: 'rgba(3,10,60,0.92)', borderBottom: '1px solid rgba(0,255,255,0.08)', position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(16px)' },
  navInner: { padding: '0 48px', height: 60, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' },
  logo: { fontFamily: "'Satoshi','Inter',sans-serif", fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: '#F8F9FA', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  navLinks: { display: 'flex', gap: 4, justifyContent: 'center' },
  navLink: { padding: '6px 12px', borderRadius: 8, color: 'rgba(248,249,250,0.82)', textDecoration: 'none', fontSize: 14, fontWeight: 500 },
  navLinkActive: { background: 'rgba(0,255,255,0.1)', color: '#00FFFF', border: '1px solid rgba(0,255,255,0.15)' },
  navRight: { display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' },
  navEmail: { fontSize: 12, color: 'rgba(248,249,250,0.72)' },
  themeBtn: { display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'rgba(248,249,250,0.82)', cursor:'pointer', padding:'6px 7px', flexShrink:0 },
  burger: { display: 'none', background: 'none', border: 'none', color: '#F8F9FA', cursor: 'pointer', padding: 4, lineHeight: 0, flexShrink: 0 },
  drawerClose: { background: 'none', border: 'none', color: 'rgba(248,249,250,0.72)', cursor: 'pointer', padding: 4, lineHeight: 0 },
  main: { maxWidth: 1100, margin: '0 auto', padding: '40px 24px' },
  mainFull: { height: 'calc(100vh - 60px)', overflow: 'hidden', display: 'flex' },
};
